import React from 'react';
import { createStyles } from '@/contexts/theme-context';
import { flexRender, HeaderGroup, SortingState } from '@tanstack/react-table';
import { TableHeaderCell } from '@experiences/components/table/table-header-cell';
import { TableHeaderRow } from '@experiences/components/table/table-header-row';
import { Typography } from '@usepepper/ui/atoms/typography';
import { View, ViewStyle } from 'react-native';

interface Props<TData> {
  /** Extra style for right-aligned header cells, e.g. a fixed header height. */
  headerCellStyle?: ViewStyle;
  headerGroup: HeaderGroup<TData>;
  isLeftAlignedColumn: (columnId: string) => boolean;
  /**
   * Read through `header.column.getIsSorted()`, not from this prop; it is
   * here so the memo re-renders when the sort changes.
   */
  sorting: SortingState;
}

// Memoized apart from the aggregate row beneath it: that row changes on every
// edit, while these cells (each with a tooltip) only change on sort.
const PricingTableHeaderCellsImpl = <TData,>({
  headerCellStyle,
  headerGroup,
  isLeftAlignedColumn,
}: Props<TData>) => {
  const [styles, theme] = useStyles();
  const rightAlignedStyle = headerCellStyle
    ? { ...styles.headerCell, ...headerCellStyle }
    : styles.headerCell;

  return (
    <TableHeaderRow>
      {headerGroup.headers.map((header) => (
        <TableHeaderCell
          columnId={header.id}
          key={header.id}
          onPress={
            header.column.getCanSort()
              ? () => header.column.toggleSorting()
              : undefined
          }
          pinnedOffset={
            header.column.getIsPinned()
              ? header.column.getStart('left')
              : undefined
          }
          sortDirection={header.column.getIsSorted() || undefined}
          style={
            isLeftAlignedColumn(header.column.id)
              ? undefined
              : rightAlignedStyle
          }
        >
          {header.isPlaceholder ? null : (
            <View style={styles.columnHeaderContainer}>
              <Typography
                color={theme.sk.color.text.tertiary}
                textAlign={
                  isLeftAlignedColumn(header.column.id) ? undefined : 'right'
                }
                variant="paragraph-sm-600"
              >
                {flexRender(
                  header.column.columnDef.header,
                  header.getContext(),
                )}
              </Typography>
            </View>
          )}
        </TableHeaderCell>
      ))}
    </TableHeaderRow>
  );
};

export const PricingTableHeaderCells = React.memo(
  PricingTableHeaderCellsImpl,
) as typeof PricingTableHeaderCellsImpl;

const useStyles = createStyles(() => ({
  columnHeaderContainer: {
    alignItems: 'center',
    flexDirection: 'row',
    flexShrink: 1,
    minWidth: 0,
  },
  headerCell: {
    alignItems: 'flex-end',
  },
}));
